import { LeftOutlined, MenuOutlined, RightOutlined } from '@ant-design/icons'
import { useGetIdentity, useLink, useMenu, useTranslate } from '@refinedev/core'
import { useThemedLayoutContext } from '@refinedev/antd'
import { Button, Drawer, Grid, Layout, Menu } from 'antd'
import type { MenuProps } from 'antd'
import type { RefineThemedLayoutV2SiderProps } from '@refinedev/antd'
import { can } from '@/providers/access-control'
import type { AdminIdentity } from '@/providers/auth-provider'
import { useAdminLocale } from '@/providers/i18n-provider'

type MenuEntry = ReturnType<typeof useMenu>['menuItems'][number]

const navigationGroups = [
  { label: 'content', resources: ['subjects', 'content_items', 'ingest'] },
  { label: 'users', resources: ['users', 'reports', 'posts'] },
  { label: 'subscriptions', resources: ['plans', 'transactions'] },
  {
    label: 'communication',
    resources: ['notifications', 'support_messages', 'cms_pages', 'faq_items'],
  },
  { label: 'system', resources: ['streams', 'admin_users', 'audit_log'] },
] as const

const findResource = (items: MenuEntry[], resource: string) =>
  items.find(item => item.name === resource)

/**
 * `Menu.items` is the supported Ant Design API. It also gives us exact resource
 * matching, so `users` can never accidentally duplicate `admin_users`.
 */
export const AdminSider = ({ Title }: RefineThemedLayoutV2SiderProps) => {
  const { data: identity } = useGetIdentity<AdminIdentity>()
  const { menuItems, selectedKey } = useMenu()
  const translate = useTranslate()
  const locale = useAdminLocale()
  const Link = useLink()
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } =
    useThemedLayoutContext()
  const breakpoint = Grid.useBreakpoint()
  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg

  const itemFor = (resource: string): NonNullable<MenuProps['items']>[number] | null => {
    const item = findResource(menuItems, resource)
    if (!item || !identity || !can(identity.role, item.name, 'list')) return null
    return {
      key: item.key,
      icon: item.icon,
      label: <Link to={item.route ?? ''}>{translate(`admin.navigation.${item.name}`)}</Link>,
    }
  }

  const dashboard = itemFor('dashboard')
  const menuEntries: MenuProps['items'] = [
    ...(dashboard ? [dashboard] : []),
    ...navigationGroups.flatMap(group => {
      const children = group.resources.map(itemFor).filter(Boolean)
      return children.length
        ? [{ type: 'group' as const, label: translate(`admin.groups.${group.label}`), children }]
        : []
    }),
  ]

  const navigation = (collapsed = false) => (
    <Menu
      mode="inline"
      inlineCollapsed={collapsed}
      selectedKeys={selectedKey ? [selectedKey] : []}
      items={menuEntries}
      onClick={() => setMobileSiderOpen(false)}
    />
  )

  if (isMobile) {
    return (
      <>
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement={locale === 'ar' ? 'right' : 'left'}
          closable={false}
          width={264}
          styles={{ body: { padding: 0 } }}
        >
          <Layout.Sider width={264} theme="light" style={{ minHeight: '100vh' }}>
            <div style={{ padding: '15px 10px' }}>{Title ? <Title collapsed={false} /> : null}</div>
            {navigation()}
          </Layout.Sider>
        </Drawer>
        <Button
          className="admin-mobile-menu-trigger"
          type="text"
          size="large"
          icon={<MenuOutlined />}
          aria-label={translate('admin.navigation.open')}
          onClick={() => setMobileSiderOpen(true)}
        />
      </>
    )
  }

  return (
    <>
      <div style={{ width: siderCollapsed ? 80 : 264, transition: 'width 150ms ease-out' }} />
      <Layout.Sider
        width={264}
        collapsedWidth={80}
        collapsible
        collapsed={siderCollapsed}
        theme="light"
        style={{
          position: 'fixed',
          insetBlockStart: 0,
          insetInlineStart: 0,
          height: '100vh',
          zIndex: 999,
        }}
        trigger={
          <Button
            type="text"
            style={{ width: '100%', height: '100%' }}
            icon={
              siderCollapsed ? (
                locale === 'ar' ? (
                  <LeftOutlined />
                ) : (
                  <RightOutlined />
                )
              ) : locale === 'ar' ? (
                <RightOutlined />
              ) : (
                <LeftOutlined />
              )
            }
            aria-label={
              siderCollapsed
                ? translate('admin.navigation.expand')
                : translate('admin.navigation.collapse')
            }
          />
        }
        onCollapse={collapsed => setSiderCollapsed(collapsed)}
      >
        <div style={{ padding: siderCollapsed ? '15px 0' : '15px 10px' }}>
          {Title ? <Title collapsed={siderCollapsed} /> : null}
        </div>
        {navigation(siderCollapsed)}
      </Layout.Sider>
    </>
  )
}
